import type { CompiledRegion } from "../../shared/codemap";
import type { FileOrderMode } from "../../shared/config";
import type { PrimaryNature } from "../../shared/guide";

const structuralStoryRank: Record<PrimaryNature, number> = {
  contract: 0,
  "controller-entrypoint": 1,
  "data-persistence": 2,
  authorization: 3,
  "business-logic": 4,
  integration: 5,
  frontend: 5,
  infrastructure: 6,
  "analytics-data-model": 6,
  "wiring-config": 7,
  other: 8,
  "tests-proof": 9,
  generated: 10,
};

const reviewPriorityRank: Record<PrimaryNature, number> = {
  authorization: 0,
  "business-logic": 1,
  "data-persistence": 2,
  integration: 3,
  infrastructure: 3,
  "analytics-data-model": 3,
  frontend: 4,
  "controller-entrypoint": 5,
  contract: 6,
  "wiring-config": 7,
  other: 8,
  "tests-proof": 9,
  generated: 10,
};

const scrutinyRank = { deep: 0, standard: 1, scan: 2 } as const;

interface FileOrderProfile {
  changedLoc: number;
  natureRank: number;
  scrutinyRank: number;
}

function profileFor(path: string, regions: CompiledRegion[], mode: FileOrderMode): FileOrderProfile {
  const matching = regions.filter((region) => region.path === path);
  const natureRanks = mode === "review-priority" ? reviewPriorityRank : structuralStoryRank;
  return {
    changedLoc: matching.reduce((total, region) => total + (region.changedLoc ?? 0), 0),
    natureRank: matching.length > 0 ? Math.min(...matching.map((region) => natureRanks[region.nature])) : natureRanks.other,
    scrutinyRank: matching.length > 0 ? Math.min(...matching.map((region) => scrutinyRank[region.scrutiny])) : scrutinyRank.scan,
  };
}

/**
 * Reorder files without changing their membership. The incoming order is the
 * Chewfile author's narrative order and remains the stable tie-breaker.
 */
export function orderStepPaths(paths: string[], regions: CompiledRegion[], mode: FileOrderMode): string[] {
  const uniquePaths = [...new Set(paths)];
  if (mode === "author-order") return uniquePaths;
  if (mode === "alphabetical") return uniquePaths.sort((left, right) => left.localeCompare(right));

  const authoredRank = new Map(uniquePaths.map((path, index) => [path, index]));
  const profiles = new Map(uniquePaths.map((path) => [path, profileFor(path, regions, mode)]));
  return uniquePaths.sort((left, right) => {
    const leftProfile = profiles.get(left)!;
    const rightProfile = profiles.get(right)!;
    if (mode === "review-priority") {
      return leftProfile.scrutinyRank - rightProfile.scrutinyRank
        || leftProfile.natureRank - rightProfile.natureRank
        || rightProfile.changedLoc - leftProfile.changedLoc
        || authoredRank.get(left)! - authoredRank.get(right)!;
    }
    return leftProfile.natureRank - rightProfile.natureRank
      || leftProfile.scrutinyRank - rightProfile.scrutinyRank
      || authoredRank.get(left)! - authoredRank.get(right)!;
  });
}
